import React, { useState, useEffect } from 'react';
import { useCurrency } from '../CurrencyContext';
import { formatValue } from '../../constants';
import { api } from '../../services/api';
import ReportDateSelector, { DateRangeOption } from './ReportDateSelector';

const UnitEconomicsCard: React.FC = () => {
  const { currencyCode } = useCurrency();
  const [range, setRange] = useState<DateRangeOption>('ytd');
  const [metrics, setMetrics] = useState({
    ltv: 0,
    cac: 0,
    ltvCacRatio: 0,
    paybackMonths: 0,
  });

  useEffect(() => {
    let isMounted = true;
    const fetchMetrics = async () => {
      try {
        const res = await api.analytics.getUnitEconomics(range);
        if (isMounted && res) {
          setMetrics(res);
        }
      } catch (err) {
        console.error('Failed to load unit economics:', err);
      }
    };
    fetchMetrics();
    return () => { isMounted = false; };
  }, [range]);

  return (
    <div id="unit-economics-card" className="bg-slate-900 rounded-3xl p-8 text-white relative overflow-hidden h-full flex flex-col justify-center shadow-lg">
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-1">
          <h2 className="text-xl font-black">Unit Economics</h2>
          <ReportDateSelector value={range} onChange={setRange} dark />
        </div>
        <p className="text-slate-400 text-sm mb-8">LTV to CAC ratio and acquisition efficiency.</p>
        
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Avg. LTV</p>
            <p className="text-2xl font-black text-emerald-400 leading-tight">{formatValue(metrics.ltv, currencyCode)}</p>
          </div>
          <div>
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Avg. CAC</p>
            <p className="text-2xl font-black text-white leading-tight">{formatValue(metrics.cac, currencyCode)}</p>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-bold text-slate-300">LTV : CAC Ratio</span>
            <span className="text-2xl font-black text-indigo-400">{metrics.ltvCacRatio}x</span>
          </div>
          <div className="w-full bg-white/10 h-2.5 rounded-full overflow-hidden">
            <div
              className="bg-indigo-500 h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(10, (metrics.ltvCacRatio / 6) * 100))}%` }}
            ></div>
          </div>
          <p className="text-[10px] text-slate-400 mt-3 font-semibold leading-relaxed">
            Target benchmark: &gt;3.0x. Payback estimated at {metrics.paybackMonths} months.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnitEconomicsCard;
